import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export function AddPostDialog({ open, onOpenChange, campaignId, onDone }) {
  const [url, setUrl] = useState("");
  const [handle, setHandle] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [autofilled, setAutofilled] = useState(false);
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef();

  // Auto-fill from TikTok oEmbed (keyless), degrades silently if unavailable.
  useEffect(() => {
    clearTimeout(timer.current);
    setAutofilled(false);
    if (!url.toLowerCase().includes("tiktok.com")) return;
    timer.current = setTimeout(async () => {
      setLooking(true);
      try {
        const { data } = await api.get("/lookup/oembed", { params: { url } });
        if (data?.data?.creator_handle) {
          setHandle((h) => h || data.data.creator_handle);
          setThumbnail(data.data.thumbnail || "");
          setAutofilled(true);
        }
      } catch { /* ignore */ }
      finally { setLooking(false); }
    }, 600);
    return () => clearTimeout(timer.current);
  }, [url]);

  const submit = async () => {
    if (!url.trim()) return toast.error("Paste a post URL");
    if (!handle.trim()) return toast.error("Add the creator handle");
    setSaving(true);
    try {
      await api.post(`/campaigns/${campaignId}/posts`, {
        post_url: url.trim(), creator_handle: handle.trim(), thumbnail: thumbnail || undefined,
      });
      toast.success("Post added to report");
      onOpenChange(false);
      setUrl(""); setHandle(""); setThumbnail(""); setAutofilled(false);
      onDone?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-post-dialog" className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add Post by URL</DialogTitle>
          <DialogDescription>Paste an Instagram Reel or TikTok video URL. TikTok details auto-fill.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Post URL</Label>
            <div className="relative">
              <Input data-testid="input-post-url" value={url} onChange={(e) => setUrl(e.target.value)} className="mt-1" placeholder="https://..." />
              {looking && <Loader2 className="absolute right-3 top-4 h-4 w-4 animate-spin text-neutral-400" />}
            </div>
          </div>
          <div>
            <Label>Creator handle</Label>
            <Input data-testid="input-post-handle" value={handle} onChange={(e) => setHandle(e.target.value)} className="mt-1" placeholder="@creator" />
          </div>
          {autofilled && (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              {thumbnail && <img src={thumbnail} alt="preview" className="h-12 w-12 rounded-lg object-cover" />}
              <p className="flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                <Sparkles className="h-3.5 w-3.5" /> Auto-filled handle & thumbnail from TikTok
              </p>
            </div>
          )}
          <Button data-testid="submit-add-post" onClick={submit} disabled={saving} className="w-full rounded-full bg-[#35606E] py-6 hover:bg-[#294b57]">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add Post
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
